import * as React from "react";

import { datadogLogs } from "@datadog/browser-logs";
import {
  CUSTOM_DOMAIN_RECIPIENT_THRESHOLD,
  DAILY_EMAILS_LIMIT,
  EmailContentType,
  MAX_RECIPIENTS_LIMIT_CUSTOM_DOMAIN,
  SendEmailDeliveryError,
  WEEKLY_EMAILS_LIMIT,
} from "@simplyk/common";
import { useSnackbar } from "notistack";
import { UseFormReturn } from "react-hook-form";

import { useSendEmailDeliveryWithSmsCode } from "../../../../common/hooks/useSendEmailDeliveryWithSmsCode";
import { EmailContent } from "../types";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { trpc } from "@/common/helpers/trpc";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
import { EmailEditorFormData } from "@/common/types/email";
import { useEmailEditorDevice } from "@/components/EmailEditorDevice/EmailEditorDeviceContext";

type UseEmailHandlersProps = {
  form: UseFormReturn<EmailEditorFormData>;
  refetch: () => Promise<void>;
  onClose: () => void;
  emailContent: EmailContent;
  thankYouEmailId?: string;
};

export const formatEmailDeliveryUpdatePayload = (data: EmailEditorFormData) => {
  const attachment = data.attachment;

  return {
    subject: data.subject,
    body: data.body,
    ...("recipientEmails" in data && typeof data.recipientEmails !== "string"
      ? { recipientEmails: data.recipientEmails }
      : {}),
    ...(Array.isArray(data.contactListIds) ? { contactListIds: data.contactListIds } : {}),
    attachmentUrl: attachment?.fileUrl || null,
  };
};

const containsDataImage = (html: string): boolean => {
  const regex = /<img[^>]*(\ssrc=["']data:|\sdata-fr-old-src=["']data:|\sdata-en-old-src=["']data:)/i;
  return regex.test(html);
};

export const useEmailEditor = ({ form, refetch, onClose, emailContent, thankYouEmailId }: UseEmailHandlersProps) => {
  const { sendEmailDelivery } = useSendEmailDeliveryWithSmsCode();
  const utils = trpc.useUtils();

  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const { logAmplitudeEvent } = useAmplitude();
  const layout = useEmailEditorDevice();
  const emailingAmplitudeParams = {
    openedFrom: emailContent.entryPoint ?? emailContent.openedFrom,
    pathname: emailContent.entryPoint ?? emailContent.openedFrom,
    emailType: emailContent.emailType,
    category: emailContent.emailType,
    flow: emailContent.flow,
    layout,
  };

  const [loading, setLoading] = React.useState(false);
  const { currentUser } = useCurrentUserContext();

  const { mutateAsync: updateEmailContentMutation } = trpc.updateEmailContent.useMutation();

  const notifyUserWithError = React.useCallback(
    (errorCode: SendEmailDeliveryError) => {
      if (errorCode === SendEmailDeliveryError.SendingFailed) {
        enqueueSnackbar(t("dashboard", "error.sendEmail.sendingFailed"), { vibe: "danger" });
      } else if (errorCode === SendEmailDeliveryError.LimitExceeded) {
        enqueueSnackbar(
          t("dashboard", "error.sendEmail.limitExceeded", { limit: MAX_RECIPIENTS_LIMIT_CUSTOM_DOMAIN }),
          {
            vibe: "danger",
          }
        );
      } else if (errorCode === SendEmailDeliveryError.CustomDomainRequired) {
        enqueueSnackbar(
          t("dashboard", "error.sendEmail.customDomainRequired", { count: CUSTOM_DOMAIN_RECIPIENT_THRESHOLD }),
          { vibe: "danger" }
        );
      } else if (errorCode === SendEmailDeliveryError.DailyLimitExceeded) {
        enqueueSnackbar(t("dashboard", "error.sendEmail.dailyLimitExceeded", { limit: DAILY_EMAILS_LIMIT }), {
          vibe: "danger",
        });
      } else if (errorCode === SendEmailDeliveryError.WeeklyLimitExceeded) {
        enqueueSnackbar(t("dashboard", "error.sendEmail.weeklyLimitExceeded", { limit: WEEKLY_EMAILS_LIMIT }), {
          vibe: "danger",
        });
      } else {
        enqueueSnackbar(t("dashboard", "error.sendEmail.unexpectedStatus"), { vibe: "danger" });
      }
    },
    [enqueueSnackbar, t]
  );

  const handleSendEmail = form.handleSubmit(async (data: EmailEditorFormData) => {
    setLoading(true);
    try {
      const emailContentId = emailContent.id;
      if (!emailContentId) {
        logAmplitudeEvent(AmplitudeEvents.EmailSendFailed, {
          ...emailingAmplitudeParams,
          errorCode: "missing_email_content_id",
        });
        datadogLogs.logger.error("Send now ran without an email content id");
        notifyUserWithError(SendEmailDeliveryError.SendingFailed);
        return;
      }

      if (data.body && containsDataImage(data.body)) {
        logAmplitudeEvent(AmplitudeEvents.EmailSendFailed, {
          ...emailingAmplitudeParams,
          errorCode: "body_contains_data_image",
        });
        enqueueSnackbar(t("dashboard", "emailDrawer.error.bodyWithImage"), { vibe: "danger", autoHideDuration: null });
        return "replace_data_image";
      }

      const payload = formatEmailDeliveryUpdatePayload(data);
      let saveErrorCode: string | undefined;
      let saveErrorMessage: string | undefined;
      let savedEmailContentId: string | undefined;
      try {
        const saved = await updateEmailContentMutation({ emailContentId, ...payload });
        savedEmailContentId = saved.data?.id;
        saveErrorCode = saved.error?.code;
        saveErrorMessage = saved.error?.message;
      } catch (error) {
        saveErrorMessage = error instanceof Error ? error.message : String(error);
      }

      if ("contactListIds" in payload) {
        void utils.getEmailContentContactLists.invalidate({ emailContentId });
      }

      if (!savedEmailContentId) {
        logAmplitudeEvent(AmplitudeEvents.EmailSendFailed, {
          ...emailingAmplitudeParams,
          errorCode: "email_content_update_failed",
        });
        datadogLogs.logger.error("Saving the email content before sending failed", {
          errorCode: saveErrorCode,
          errorMessage: saveErrorMessage,
        });
        notifyUserWithError(SendEmailDeliveryError.SendingFailed);
        return;
      }

      if (emailContent.emailType === EmailContentType.CRMWORKFLOW) {
        enqueueSnackbar({ vibe: "positive", message: t("dashboard", "changesSaved") });
        void refetch?.();
        onClose();
        return;
      }

      if (!currentUser) {
        logAmplitudeEvent(AmplitudeEvents.EmailSendFailed, {
          ...emailingAmplitudeParams,
          errorCode: SendEmailDeliveryError.SendingFailed,
        });
        datadogLogs.logger.error(SendEmailDeliveryError.SendingFailed);
        notifyUserWithError(SendEmailDeliveryError.SendingFailed);
        return;
      }

      // Use SMS code protected mutation for 2FA when threshold is exceeded
      const { error, isCancelled } = await sendEmailDelivery({
        emailContentId,
        isPreview: false,
        ...(thankYouEmailId ? { thankYouTransactionId: thankYouEmailId } : {}),
      });

      // If 2FA was cancelled, just stop without showing any message
      if (isCancelled) {
        return;
      }

      if (error) {
        logAmplitudeEvent(AmplitudeEvents.EmailSendFailed, { ...emailingAmplitudeParams, errorCode: error?.code });
        notifyUserWithError(error?.code as SendEmailDeliveryError);
        return error?.code;
      }

      logAmplitudeEvent(AmplitudeEvents.EmailSent, emailingAmplitudeParams);
      enqueueSnackbar({
        vibe: "positive",
        message: t("dashboard", "email.delivered"),
      });

      const shouldInvalidatePayments =
        emailContent.emailType === EmailContentType.EXTRA_THANK_YOU_EMAIL ||
        emailContent.emailType === EmailContentType.INCOMPLETE_PAYMENT_FOLLOW_UP;
      if (shouldInvalidatePayments) {
        void utils.getCampaignPayments.invalidate();
      }
      if (thankYouEmailId) {
        void utils.getPaymentCommunications.invalidate();
      }

      void refetch?.();
      onClose();
    } finally {
      setLoading(false);
    }
  });

  return {
    handleSendEmail,
    loading,
  };
};
